// src/receptionist/components/OpenerEditor.tsx
//
// The first thing a customer ever reads (Conversation → Opener). Three greetings,
// because the right opening line depends on what the AI already knows: whether
// the shop is open, and whether it has met this person before.
//
// Laid out as the Follow-ups are (29 Oct 2026): one card per greeting, saying
// when it is used and quoting it; opened, the text on the left and the WhatsApp
// bubble on the right, instead of a full-width box and a full-width preview
// stacked under it. These go out inside the 24-hour window, so they are free
// text: no template and no Meta approval.

import { useState } from "react";
import { ChevronRightIcon, ListChecksIcon, MoonIcon, RepeatIcon, SunIcon, type LucideIcon } from "lucide-react";
import { Textarea } from "@component/textarea";
import { Switch } from "@component/switch";
import { FOCUS_RING, SURFACE, TYPE } from "@oura/ui";
import { cn } from "@oura/lib/cn";
import { WaPreview } from "./ui";
import { InfoNotice } from "./kit";
import type { Opener } from "../flow";

type GreetingKey = "newCustomerOpen" | "newCustomerClosed" | "returningCustomer";

const GREETINGS: { key: GreetingKey; title: string; when: string; why: string; icon: LucideIcon }[] = [
    {
        key: "newCustomerOpen",
        title: "New customer, while you are open",
        when: "Used when someone messages for the first time during opening hours.",
        why: "The everyday greeting.",
        icon: SunIcon,
    },
    {
        key: "newCustomerClosed",
        title: "New customer, after hours",
        when: "Used when someone messages for the first time while you are closed.",
        why: "Say that your team confirms when you open, so nobody waits for an instant answer.",
        icon: MoonIcon,
    },
    {
        key: "returningCustomer",
        title: "Returning customer",
        when: "Used for someone the receptionist has booked before.",
        why: "A shortcut straight to a rebooking.",
        icon: RepeatIcon,
    },
];

function GreetingCard({
    title, when, why, icon: Icon, value, onChange, disabled, startOpen,
}: {
    title: string;
    when: string;
    why: string;
    icon: LucideIcon;
    value: string;
    onChange: (v: string) => void;
    disabled?: boolean;
    startOpen: boolean;
}) {
    const [open, setOpen] = useState(startOpen);
    return (
        <section className={cn(SURFACE.card, "p-5")}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                className={cn("flex w-full items-start gap-3 rounded-md text-left", FOCUS_RING)}
            >
                <span aria-hidden className="grid h-8 w-8 shrink-0 place-items-center rounded-[10px] bg-[#E8F1FF] text-[#0A5FD1]">
                    <Icon size={15} strokeWidth={2} />
                </span>
                <span className="min-w-0 flex-1">
                    <span className={cn(TYPE.tb, "flex items-center gap-1.5 font-semibold text-gray-900")}>
                        {title}
                        <ChevronRightIcon aria-hidden size={14} className={cn("shrink-0 text-gray-400 transition-transform", open && "rotate-90")} />
                    </span>
                    <span className={cn(TYPE.tc, "mt-0.5 block text-gray-500")}>{when}</span>
                    {!open && (
                        <span className={cn(TYPE.tc, "mt-1 block truncate", value.trim() ? "text-gray-700" : "italic text-gray-400")}>
                            {value.trim() ? `“${value.trim()}”` : "Nothing written yet: the receptionist improvises."}
                        </span>
                    )}
                </span>
            </button>

            {open && (
                <div className="mt-4 grid gap-5 border-t border-gray-100 pt-4 lg:grid-cols-[minmax(0,1fr)_300px]">
                    <div className="min-w-0 space-y-1.5">
                        <p className={cn(TYPE.lab, "m-0")}>Message</p>
                        <Textarea
                            value={value}
                            disabled={disabled}
                            onChange={(e) => onChange(e.target.value)}
                            placeholder="What the receptionist opens with"
                            aria-label={title}
                            className="min-h-[120px] resize-y"
                        />
                        <p className={cn(TYPE.tc, "m-0 text-gray-500")}>{why}</p>
                    </div>
                    <div>
                        <p className={cn(TYPE.lab, "m-0 mb-1.5")}>What the customer sees</p>
                        <WaPreview text={value} note={when.replace(/^Used /, "").replace(/\.$/, "")} placeholder="Nothing set, so the receptionist improvises." />
                    </div>
                </div>
            )}
        </section>
    );
}

export function OpenerEditor({
    opener, onChange, disabled,
}: { opener: Opener; onChange: (o: Opener) => void; disabled?: boolean }) {
    const patch = (next: Partial<Opener>) => onChange({ ...opener, ...next });

    return (
        <div className="space-y-3">
            <InfoNotice>
                The receptionist's first reply, before any booking question. It goes out inside WhatsApp's 24-hour window, so it is free
                text: no template and no approval needed.
            </InfoNotice>

            {GREETINGS.map((g, i) => (
                <GreetingCard
                    key={g.key}
                    title={g.title}
                    when={g.when}
                    why={g.why}
                    icon={g.icon}
                    value={opener[g.key]}
                    onChange={(v) => patch({ [g.key]: v } as Partial<Opener>)}
                    disabled={disabled}
                    startOpen={i === 0}
                />
            ))}

            {/* A switch, not a checkbox: a setting that takes effect, as everywhere else in the product. */}
            <section className={cn(SURFACE.card, "flex items-start gap-3 p-5")}>
                <span aria-hidden className="grid h-8 w-8 shrink-0 place-items-center rounded-[10px] bg-[#E8F1FF] text-[#0A5FD1]">
                    <ListChecksIcon size={15} strokeWidth={2} />
                </span>
                <div className="min-w-0 flex-1">
                    <p className={cn(TYPE.tb, "m-0 font-semibold text-gray-900")}>List your bookable services in the first reply</p>
                    <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>
                        Good for a short menu. With a long price list, it is better to let the customer ask.
                    </p>
                </div>
                <Switch
                    checked={opener.offerServiceMenu}
                    disabled={disabled}
                    onCheckedChange={(v) => patch({ offerServiceMenu: v })}
                    aria-label="List your bookable services in the first reply"
                    className="mt-1 flex-shrink-0"
                />
            </section>
        </div>
    );
}
